import { User } from "firebase/auth";
import {
  KrogerLocation,
  KrogerLocationsSearchResponse,
  KrogerLocationSelectResponse,
  KrogerSelectedLocationResponse,
} from "../types/kroger";

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Kroger's location search only takes a 5-digit ZIP (ZIP+4 is trimmed).
 */
export const isValidZipCode = (value: string): boolean =>
  /^\d{5}(-\d{4})?$/.test(value.trim());

/**
 * Search for Kroger store locations by ZIP code
 */
export const searchKrogerLocations = async (
  zipCode: string,
  radiusMiles: number = 25
): Promise<KrogerLocation[]> => {
  try {
    const params = new URLSearchParams({
      zipCode,
      radiusMiles: radiusMiles.toString(),
    });

    const response = await fetch(
      `${API_BASE_URL}/api/kroger/locations/search?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search locations: ${response.status}`);
    }

    const data: KrogerLocationsSearchResponse = await response.json();
    return data.locations;
  } catch (error) {
    console.error("Error searching Kroger locations:", error);
    throw new Error("Failed to search for store locations. Please try again.");
  }
};

/**
 * Search for Kroger store locations by latitude/longitude
 */
export const searchKrogerLocationsByCoords = async (
  latitude: number,
  longitude: number,
  radiusMiles: number = 25
): Promise<KrogerLocation[]> => {
  try {
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      radiusMiles: radiusMiles.toString(),
    });

    const response = await fetch(
      `${API_BASE_URL}/api/kroger/locations/search?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search locations: ${response.status}`);
    }

    const data: KrogerLocationsSearchResponse = await response.json();
    return data.locations;
  } catch (error) {
    console.error("Error searching Kroger locations by coordinates:", error);
    throw new Error("Failed to search for store locations. Please try again.");
  }
};

/**
 * Set the user's selected Kroger location and fulfillment preference
 */
export const selectKrogerLocation = async (
  user: User,
  locationId: string,
  fulfillmentPreference?: "pickup" | "delivery"
): Promise<void> => {
  if (!user) {
    throw new Error("You must be signed in to select a store location.");
  }

  try {
    const token = await user.getIdToken();
    const response = await fetch(
      `${API_BASE_URL}/api/kroger/locations/select`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ locationId, fulfillmentPreference }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to select location: ${response.status}`);
    }

    const data: KrogerLocationSelectResponse = await response.json();
    if (!data.success) {
      throw new Error("Failed to save selected location");
    }
  } catch (error) {
    console.error("Error selecting Kroger location:", error);
    throw new Error("Failed to save your store selection. Please try again.");
  }
};

/**
 * Get the user's currently selected Kroger location
 */
export const getSelectedKrogerLocation = async (
  user: User
): Promise<string | null> => {
  if (!user) {
    return null;
  }

  try {
    const token = await user.getIdToken();
    const response = await fetch(
      `${API_BASE_URL}/api/kroger/locations/selected`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.status === 404) {
      // No location selected yet
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to get selected location: ${response.status}`);
    }

    const data: KrogerSelectedLocationResponse = await response.json();
    return data.locationId;
  } catch (error) {
    console.error("Error getting selected Kroger location:", error);
    // Don't throw here - just return null if we can't get the location
    return null;
  }
};
